/**
 * Preview Handler - Secure draft and review content access for child app
 * Handles preview tokens and loads content with proper access control
 */

// Determine API base URL (same logic as api.js)
const getApiBaseUrl = () => {
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8000';
    }

    if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
        return 'http://localhost:8000';
    }

    return 'https://api.kassie.media';
};

const API_BASE_URL = getApiBaseUrl();

class PreviewHandler {
    constructor() {
        this.currentToken = null;
        this.tokenExpiry = null;
        this.moduleId = null;
        this.contentState = null;
        this.previewData = null;
    }

    /**
     * Initialize preview mode from URL parameters
     */
    async initializePreview() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            const state = urlParams.get('state');

            if (!token) {
                this.showError('No preview token provided');
                return false;
            }

            // Store token and attempt to load content
            this.currentToken = token;
            this.contentState = state;

            const success = await this.loadPreviewContent();
            if (success) {
                this.setupPreviewUI();
                this.startTokenExpiryMonitoring();

                // Navigate to story reader with preview content
                await this.showPreviewContent();
            }

            return success;
        } catch (error) {
            console.error('Failed to initialize preview:', error);
            this.showError('Failed to initialize preview mode');
            return false;
        }
    }

    /**
     * Load preview content using the token
     */
    async loadPreviewContent() {
        try {
            if (!this.currentToken) {
                throw new Error('No preview token available');
            }

            // Extract module ID from JWT token
            this.moduleId = this.extractModuleIdFromToken();
            if (!this.moduleId) {
                throw new Error('Could not determine module ID from token');
            }

            console.log('📦 Loading preview content for module:', this.moduleId);

            // Load the content using the module ID and token
            const contentResponse = await this.makePreviewRequest(`/api/preview/content/${this.moduleId}`, {
                token: this.currentToken
            });

            if (!contentResponse) {
                throw new Error('Failed to load preview content');
            }

            this.previewData = contentResponse;
            this.moduleId = contentResponse.id || this.moduleId;

            // Extract preview metadata
            const previewMeta = contentResponse._preview || {};
            this.contentState = previewMeta.state || this.contentState;
            this.tokenExpiry = previewMeta.token_expires_at ? new Date(previewMeta.token_expires_at) : null;

            console.log('✅ Preview content loaded:', {
                moduleId: this.moduleId,
                contentState: this.contentState,
                tokenExpiry: this.tokenExpiry
            });

            return true;
        } catch (error) {
            console.error('Failed to load preview content:', error);

            if (error.status === 401) {
                this.showError('Preview token expired or invalid');
            } else if (error.status === 403) {
                this.showError('Access denied for this preview');
            } else if (error.status === 404) {
                this.showError('Preview content not found');
            } else {
                this.showError('Failed to load preview content');
            }

            return false;
        }
    }

    /**
     * Make a request with preview token
     */
    async makePreviewRequest(endpoint, params = {}) {
        try {
            const url = new URL(endpoint, API_BASE_URL);

            // Add token and other parameters
            Object.keys(params).forEach(key => {
                url.searchParams.append(key, params[key]);
            });

            console.log('🔗 Preview API request:', url.toString());

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                const error = new Error(`HTTP ${response.status}`);
                error.status = response.status;
                throw error;
            }

            return await response.json();
        } catch (error) {
            console.error('Preview request failed:', error);
            throw error;
        }
    }

    /**
     * Extract module ID from JWT token (basic decode, no verification)
     */
    extractModuleIdFromToken() {
        try {
            if (!this.currentToken) return null;

            // Basic JWT decode (just for extracting payload, not for verification)
            const parts = this.currentToken.split('.');
            if (parts.length !== 3) return null;

            const payload = JSON.parse(atob(parts[1]));
            return payload.module_id || null;
        } catch (error) {
            console.error('Failed to extract module ID from token:', error);
            return null;
        }
    }

    /**
     * Setup preview UI indicators
     */
    setupPreviewUI() {
        // Add preview banner
        this.addPreviewBanner();

        // Add preview styles
        this.addPreviewStyles();

        // Override asset loading to use preview endpoints
        this.setupAssetInterception();
    }

    /**
     * Add preview mode banner
     */
    addPreviewBanner() {
        const banner = document.createElement('div');
        banner.id = 'preview-banner';
        banner.className = 'preview-banner';

        const stateLabel = this.contentState === 'review' ? 'Review' : 'Draft';
        const stateClass = this.contentState === 'review' ? 'review' : 'draft';

        banner.innerHTML = `
      <div class="preview-banner-content">
        <div class="preview-info">
          <span class="preview-icon">👁️</span>
          <span class="preview-text">Preview Mode</span>
          <span class="preview-state ${stateClass}">${stateLabel}</span>
        </div>
        <div class="preview-expiry" id="previewExpiry">
          ${this.getExpiryText()}
        </div>
        <button class="preview-close" onclick="previewHandler.exitPreview()">×</button>
      </div>
    `;

        document.body.insertBefore(banner, document.body.firstChild);
    }

    /**
     * Add preview-specific styles
     */
    addPreviewStyles() {
        const style = document.createElement('style');
        style.textContent = `
      .preview-banner {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 8px 16px;
        z-index: 10000;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .preview-banner-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        max-width: 1200px;
        margin: 0 auto;
      }

      .preview-info {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .preview-icon {
        font-size: 16px;
      }

      .preview-text {
        font-weight: 600;
        font-size: 14px;
      }

      .preview-state {
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 500;
        text-transform: uppercase;
      }

      .preview-state.draft {
        background: rgba(255, 193, 7, 0.2);
        border: 1px solid rgba(255, 193, 7, 0.5);
      }

      .preview-state.review {
        background: rgba(0, 123, 255, 0.2);
        border: 1px solid rgba(0, 123, 255, 0.5);
      }

      .preview-expiry {
        font-size: 12px;
        opacity: 0.9;
      }

      .preview-close {
        background: none;
        border: none;
        color: white;
        font-size: 20px;
        cursor: pointer;
        padding: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background-color 0.2s;
      }

      .preview-close:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      /* Adjust body padding to account for banner */
      body.preview-mode {
        padding-top: 40px !important;
      }

      /* Preview content styling */
      .preview-content {
        border: 2px dashed rgba(102, 126, 234, 0.3);
        border-radius: 8px;
        margin: 16px;
        position: relative;
      }

      .preview-content::before {
        content: "Preview Content";
        position: absolute;
        top: -12px;
        left: 16px;
        background: white;
        padding: 0 8px;
        font-size: 12px;
        color: #667eea;
        font-weight: 500;
      }
    `;

        document.head.appendChild(style);
        document.body.classList.add('preview-mode');
    }

    /**
     * Setup asset URL interception for preview mode
     */
    setupAssetInterception() {
        // Override image loading to use preview asset endpoints
        const originalCreateElement = document.createElement;

        document.createElement = function (tagName) {
            const element = originalCreateElement.call(this, tagName);

            if (tagName.toLowerCase() === 'img') {
                const originalSetAttribute = element.setAttribute;
                element.setAttribute = function (name, value) {
                    if (name === 'src' && value && previewHandler.shouldInterceptAsset(value)) {
                        value = previewHandler.convertToPreviewAssetUrl(value);
                    }
                    return originalSetAttribute.call(this, name, value);
                };
            }

            return element;
        };
    }

    /**
     * Check if an asset URL should be intercepted for preview
     */
    shouldInterceptAsset(url) {
        // Intercept URLs that look like module assets
        return url && (
            url.includes('/modules/') ||
            url.includes('/drafts/') ||
            url.startsWith('blob:') ||
            (this.moduleId && url.includes(this.moduleId))
        );
    }

    /**
     * Convert regular asset URL to preview asset URL
     */
    convertToPreviewAssetUrl(originalUrl) {
        try {
            if (!this.moduleId || !this.currentToken) {
                return originalUrl;
            }

            // Extract asset path from original URL
            let assetPath = originalUrl;

            // Handle different URL formats
            if (originalUrl.includes('/modules/')) {
                assetPath = originalUrl.split('/modules/')[1];
                if (assetPath.startsWith(this.moduleId + '/')) {
                    assetPath = assetPath.substring(this.moduleId.length + 1);
                }
            } else if (originalUrl.includes('/drafts/')) {
                assetPath = originalUrl.split('/drafts/')[1];
                if (assetPath.startsWith(this.moduleId + '/')) {
                    assetPath = assetPath.substring(this.moduleId.length + 1);
                }
            }

            // Build preview asset URL
            const previewUrl = `/api/preview/assets/${this.moduleId}/${assetPath}?token=${this.currentToken}`;

            console.log('🔄 Converting asset URL:', originalUrl, '→', previewUrl);
            return previewUrl;
        } catch (error) {
            console.error('Failed to convert asset URL:', error);
            return originalUrl;
        }
    }

    /**
     * Start monitoring token expiry
     */
    startTokenExpiryMonitoring() {
        if (!this.tokenExpiry) return;

        // Update expiry display every minute
        this.expiryInterval = setInterval(() => {
            this.updateExpiryDisplay();
        }, 60000);

        // Check for expiry every 30 seconds
        this.expiryCheckInterval = setInterval(() => {
            if (new Date() >= this.tokenExpiry) {
                this.handleTokenExpiry();
            }
        }, 30000);
    }

    /**
     * Update expiry display in banner
     */
    updateExpiryDisplay() {
        const expiryElement = document.getElementById('previewExpiry');
        if (expiryElement) {
            expiryElement.textContent = this.getExpiryText();
        }
    }

    /**
     * Get expiry text for display
     */
    getExpiryText() {
        if (!this.tokenExpiry) {
            return 'No expiry info';
        }

        const now = new Date();
        const timeLeft = this.tokenExpiry - now;

        if (timeLeft <= 0) {
            return 'Expired';
        }

        const minutes = Math.floor(timeLeft / (1000 * 60));
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `Expires in ${hours}h ${minutes % 60}m`;
        } else {
            return `Expires in ${minutes}m`;
        }
    }

    /**
     * Handle token expiry
     */
    handleTokenExpiry() {
        this.clearIntervals();
        this.showError('Preview session expired. Please generate a new preview link.');

        // Disable further interactions
        document.body.style.pointerEvents = 'none';
        document.body.style.opacity = '0.5';
    }

    /**
     * Exit preview mode
     */
    exitPreview() {
        this.clearIntervals();

        // Try to close the window
        if (window.opener) {
            window.close();
        } else {
            // If can't close, redirect to a safe page
            window.location.href = '/';
        }
    }

    /**
     * Clear monitoring intervals
     */
    clearIntervals() {
        if (this.expiryInterval) {
            clearInterval(this.expiryInterval);
            this.expiryInterval = null;
        }

        if (this.expiryCheckInterval) {
            clearInterval(this.expiryCheckInterval);
            this.expiryCheckInterval = null;
        }
    }

    /**
     * Show error message
     */
    showError(message) {
        console.error('Preview error:', message);

        // Create error overlay
        const overlay = document.createElement('div');
        overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 20000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

        overlay.innerHTML = `
      <div style="text-align: center; padding: 32px; background: #dc3545; border-radius: 8px; max-width: 400px;">
        <h3 style="margin: 0 0 16px 0;">Preview Error</h3>
        <p style="margin: 0 0 24px 0;">${message}</p>
        <button onclick="previewHandler.exitPreview()" style="
          background: white;
          color: #dc3545;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        ">Close Preview</button>
      </div>
    `;

        document.body.appendChild(overlay);
    }

    /**
     * Show preview content in story reader
     */
    async showPreviewContent() {
        try {
            console.log('📖 Showing preview content in reader');

            // Wait for content container to be available (created by app-new.js)
            const contentContainer = await this.waitForElement('content-container', 5000);
            if (!contentContainer) {
                throw new Error('Content container not found - app not initialized');
            }

            // Import the story reader
            const { renderStoryReader } = await import('./views/StoryReader.js');

            // Create a guest profile for preview mode
            const previewProfile = {
                id: 'preview',
                name: 'Preview',
                isGuest: true,
                isPreview: true
            };

            // Create module data from preview content
            const moduleData = {
                id: this.moduleId,
                title: this.previewData.title || 'Preview',
                coverImageUrl: this.previewData.coverImageUrl || this.previewData.thumbnailUrl || null,
                thumbnailUrl: this.previewData.thumbnailUrl || this.previewData.coverImageUrl || null,
                storyContent: {
                    title: this.previewData.title,
                    pages: this.previewData.storyPages || [],
                    coverImage: this.previewData.coverImageUrl || this.previewData.thumbnailUrl || null
                }
            };

            // Render the story reader
            await renderStoryReader(previewProfile, moduleData);

            console.log('✅ Preview content displayed in reader');
        } catch (error) {
            console.error('Failed to show preview content:', error);
            this.showError('Failed to display preview content');
        }
    }

    /**
     * Wait for an element to appear in the DOM
     */
    waitForElement(elementId, timeout = 5000) {
        return new Promise((resolve) => {
            const element = document.getElementById(elementId);
            if (element) {
                resolve(element);
                return;
            }

            const observer = new MutationObserver(() => {
                const element = document.getElementById(elementId);
                if (element) {
                    observer.disconnect();
                    resolve(element);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Timeout after specified time
            setTimeout(() => {
                observer.disconnect();
                resolve(null);
            }, timeout);
        });
    }

    /**
     * Get preview data for use by other components
     */
    getPreviewData() {
        return this.previewData;
    }

    /**
     * Check if currently in preview mode
     */
    isPreviewMode() {
        return !!this.currentToken;
    }
}

// Create global instance
const previewHandler = new PreviewHandler();

// Auto-initialize if preview parameters are present
document.addEventListener('DOMContentLoaded', () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('token')) {
        previewHandler.initializePreview();
    }
});

// Export for use in other modules
export { previewHandler };

// Make available globally for onclick handlers
window.previewHandler = previewHandler;
